'use server';

/**
 * Kontora BFF – Active-Context Server Action
 *
 * Persistiert den aktiven Top-Level-Kontext und den Power-Mode in Cookies.
 * - Active-Context: "kontora_active_context"
 * - Power-Mode:     "kontora_power_mode"  (nur für SUPER_ADMIN setzbar)
 *
 * Cookies werden nicht als HttpOnly gesetzt, damit der Client sie lesen
 * und z. B. für clientseitige Hervorhebungen nutzen kann.
 * Same-Site=Lax + Path=/ reicht für CSRF aus, weil das Setzen
 * per Server Action zusätzlich durch CSRF-Token geschützt ist.
 */

import { cookies } from 'next/headers';
import { revalidatePath } from 'next/cache';
import {
  ACTIVE_CONTEXT_COOKIE,
  POWER_MODE_COOKIE,
  POWER_MODE_COOKIE_MAX_AGE,
} from '@/src/server/ui-context/ui-context.helper';
import {
  DEFAULT_NAVIGATION_CONTEXT,
  isNavigationContext,
  NAVIGATION_CONTEXTS,
  type NavigationContext,
} from '@/src/lib/navigation';
import { deriveRoleLevel } from '@/src/lib/roles';
import { getCurrentSession } from '@/src/server/session/session.helper';
import { callInternalApi } from '@/src/server/api-client/internal-api-client';
import { getSessionToken } from '@/src/server/session/session.helper';

const COOKIE_COMMON = {
  path: '/',
  sameSite: 'lax' as const,
  httpOnly: false,
  secure: process.env['NODE_ENV'] === 'production',
};

export interface SetActiveContextResult {
  ok: boolean;
  activeContext: NavigationContext;
  powerMode: boolean;
  error?: string;
}

function normalizeContext(value: unknown): NavigationContext {
  if (isNavigationContext(value)) return value;
  return DEFAULT_NAVIGATION_CONTEXT;
}

async function loadCurrentRoles(): Promise<string[]> {
  const session = await getCurrentSession();
  return session?.roles ?? [];
}

async function ensureCsrfCookie(): Promise<void> {
  const token = await getSessionToken();
  if (!token) return;
  try {
    await callInternalApi('GET', '/csrf', { sessionToken: token });
  } catch {
    // ignore – CSRF wird vom Next.js Server-Action-Runner zusätzlich geprüft
  }
}

export async function setActiveContextAction(
  formData: FormData,
): Promise<SetActiveContextResult> {
  await ensureCsrfCookie();

  const roles = await loadCurrentRoles();
  const roleLevel = deriveRoleLevel(roles);

  const requested = formData.get('context');
  const normalized = normalizeContext(requested);

  const requiredLevel = NAVIGATION_CONTEXTS[normalized].requiredRoleLevel;
  const rank = { readonly: 1, user: 2, manager: 3, admin: 4, superuser: 5 } as const;
  const userRank = rank[roleLevel];

  if (userRank < rank[requiredLevel]) {
    return {
      ok: false,
      activeContext: DEFAULT_NAVIGATION_CONTEXT,
      powerMode: false,
      error: 'Kontext für diese Rolle nicht erlaubt',
    };
  }

  const cookieStore = await cookies();
  cookieStore.set({
    ...COOKIE_COMMON,
    name: ACTIVE_CONTEXT_COOKIE,
    value: normalized,
    maxAge: 60 * 60 * 24 * 30, // 30d
  });

  // Wenn der Benutzer den Kontext wechselt, wird der Power-Mode ausgeschaltet
  // (Power-Mode ist eine bewusste Aktion und nicht kontextspezifisch).
  const existingPowerMode = cookieStore.get(POWER_MODE_COOKIE)?.value;
  if (existingPowerMode === '1') {
    cookieStore.set({
      ...COOKIE_COMMON,
      name: POWER_MODE_COOKIE,
      value: '0',
      maxAge: POWER_MODE_COOKIE_MAX_AGE,
    });
  }

  revalidatePath('/', 'layout');

  return {
    ok: true,
    activeContext: normalized,
    powerMode: false,
  };
}

export async function setPowerModeAction(
  formData: FormData,
): Promise<SetActiveContextResult> {
  await ensureCsrfCookie();

  const roles = await loadCurrentRoles();
  const roleLevel = deriveRoleLevel(roles);

  if (roleLevel !== 'superuser') {
    return {
      ok: false,
      activeContext: normalizeContext(formData.get('context')),
      powerMode: false,
      error: 'Power-Mode nur für SUPER_ADMIN verfügbar',
    };
  }

  const requested = formData.get('enabled');
  const enabled = requested === 'true' || requested === '1' || requested === 'on';
  const context = normalizeContext(formData.get('context'));

  const cookieStore = await cookies();
  cookieStore.set({
    ...COOKIE_COMMON,
    name: POWER_MODE_COOKIE,
    value: enabled ? '1' : '0',
    maxAge: POWER_MODE_COOKIE_MAX_AGE,
  });

  // Kontext mitführen, damit der Power-Mode nicht den falschen Bereich erzwingt
  if (context !== DEFAULT_NAVIGATION_CONTEXT) {
    cookieStore.set({
      ...COOKIE_COMMON,
      name: ACTIVE_CONTEXT_COOKIE,
      value: context,
      maxAge: 60 * 60 * 24 * 30,
    });
  }

  revalidatePath('/', 'layout');

  return {
    ok: true,
    activeContext: context,
    powerMode: enabled,
  };
}